import { ClientRequestGroupStatus, TypedIdentity } from '@aiwyn/common/client-requests';
import {
  ClientRequestDocumentActivityFile,
  ClientRequestGroupType,
  ClientRequestItemStatus,
  RequestSubmissionFileStatus,
} from '@aiwyn/api';
import { FileDisplayStatus as ClientRequestDocumentFileDisplayStatus, RecursivePartial } from '@aiwyn/shared';

export { FileDisplayStatus as ClientRequestDocumentFileDisplayStatus } from '@aiwyn/shared';

//#region Query Params

export interface ClientRequestQueryParams {
  group?: TypedIdentity<ClientRequestGroupType>;
  request?: string;
  deliverable?: string;
}

//#endregion

//#region Filters

export enum ClientRequestFilterType {
  STATUS = 'Status',
  SERVICE = 'Services',
  ACCOUNT = 'Accounts',
  ITEM_STATUS = 'RequestStatus,'
}

export type ClientRequestFilters = Partial<RawClientRequestFilters>;

interface RawClientRequestFilters {
  [ClientRequestFilterType.STATUS]: ClientRequestGroupStatus[];
  [ClientRequestFilterType.SERVICE]: number[];
  [ClientRequestFilterType.ACCOUNT]: number[];
  [ClientRequestFilterType.ITEM_STATUS]: RequestItemStatusFilterOption[];
}

export const PROJECT_LABEL = 'project' as const;

//#region Item Status Filters

export const REQUEST_ITEM_STATUS_FILTER_OPTIONS = [
  ClientRequestItemStatus.OPEN,
  ClientRequestItemStatus.IN_REVIEW,
  ClientRequestItemStatus.REJECTED,
  ClientRequestItemStatus.DONE,
] as const;

// type that is a union of the values in REQUEST_ITEM_STATUS_FILTERS
export type RequestItemStatusFilterOption = typeof REQUEST_ITEM_STATUS_FILTER_OPTIONS[number];

/**
 * Represents the status filters state for a client request.
 * Each key is a ClientRequestItemStatus and the value is a boolean indicating if that status is included in the filter.
 */
export type RequestItemStatusFilters = Record<RequestItemStatusFilterOption, boolean>;

export const REQUEST_ITEM_DUE_DATE_FILTER_OPTIONS = ['overdue', 'dueToday', 'dueThisWeek'] as const;

export type RequestItemDueDateFilterOption = typeof REQUEST_ITEM_DUE_DATE_FILTER_OPTIONS[number];

/**
 * Represents the due date filters state for a client request.
 * Each key is a due date filter option and the value is a boolean indicating if that filter is included.
 */
export type RequestItemDueDateFilters = Record<RequestItemDueDateFilterOption, boolean>;

export interface RequestItemFilters {
  status: RequestItemStatusFilters;
  dueDate: RequestItemDueDateFilters;
  // isApplicable: boolean; // TODO: May be implemented in the future
}
/**
 * Represents the filters state for the items of each client request.
 * Each key is a client request ID, and the value is the state of the corresponding request's item filters.
 * Looks like this:
 * {
 *   1: {
 *     status: {
 *       [ClientRequestItemStatus.OPEN]: true,
 *       [ClientRequestItemStatus.IN_REVIEW]: false,
 *     },
 *     dueDate: {
 *       overdue: true,
 *     },
 *   },
 *   1: {
 *     status: {
 *       [ClientRequestItemStatus.DONE]: true,
 *     },
 *     // isApplicable: true,
 *   },
 *   ...
 * }
 */
export type AllRequestItemFilters = Record<number, RecursivePartial<RequestItemFilters>>; // actually a `NonemptyRecursivePartial` but TS is hard

//#endregion

//#endregion

//#region File

export function getOriginalBoxFileName(fileName: string): string {
  // Use a regular expression to match the numeric suffix with an underscore
  const regex = /_(\d+)(\.[a-zA-Z0-9]+)$/;

  // Replace the matched numeric suffix with an empty string
  return fileName.replace(regex, '$2'); // Keep the file extension intact
}

export interface ClientRequestDocumentFileDisplay {
  id: number;
  dmsId: string;
  name: string;
  status?: ClientRequestDocumentFileDisplayStatus;
  isFlagged?: boolean;
  requestItemId?: number;
  dmsUploadFileJobId?: string; // Upload
  bytesSize?: number;
  isMasked?: boolean;
  createdAt?: string;
}

export const RequestSubmissionFileStatusToClientRequestDocumentFileDisplayStatus: Record<
  RequestSubmissionFileStatus,
  ClientRequestDocumentFileDisplayStatus
> = {
  [RequestSubmissionFileStatus.CATEGORIZING]: ClientRequestDocumentFileDisplayStatus.CATEGORIZING,
  [RequestSubmissionFileStatus.UNCATEGORIZED]: ClientRequestDocumentFileDisplayStatus.UNCATEGORIZED,
  [RequestSubmissionFileStatus.CATEGORIZED]: ClientRequestDocumentFileDisplayStatus.CATEGORIZED,
  [RequestSubmissionFileStatus.AUTO_CATEGORIZED]: ClientRequestDocumentFileDisplayStatus.CATEGORIZED,
  [RequestSubmissionFileStatus.UPLOADING]: ClientRequestDocumentFileDisplayStatus.UPLOADING,
};

//#endregion

//#region Deliverable

export const ALL_DELIVERABLES = 'all';
export type AllDeliverables = typeof ALL_DELIVERABLES;
export type FocusedDeliverableId = number | AllDeliverables;

//#endregion

/**
 * Normalized view model for client request activity items
 * Handles API differences and provides consistent interface for components
 */
export interface ClientRequestItemActivityViewModel {
  // Common normalized fields (handled by selector)
  itemId: number;
  itemName?: string;
  rejectedAt: string;
  status: string;
  submittedAt: string;
  userId: number; // Normalized from user_id/userId
  userName: string; // Normalized from user_name/userName
  userType: string; // Normalized from user_type/userType
  rejectionNotes: string; // Normalized from rejection_notes/rejectionNotes
  activityType: 'answer' | 'document';

  // Type-specific fields (optional, but type-safe via activityType check)
  lastAnswerBeforeRejection?: string; // Only for answer type
  notes?: string; // Only for document type
  files?: ClientRequestDocumentActivityFile[]; // Only for document type
}
